import React from 'react';
import { Mail, Phone, Globe, Zap, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = () => {
  const brandGold = "#ffb700";

  // Reusable animation config for staggered rendering
  const fadeUpVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: { delay: custom * 0.1, duration: 0.5, ease: "easeOut" }
    })
  };

  return (
    <footer className="bg-[#020408] border-t border-white/5 pt-20 pb-10 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mb-16 text-left">
        
        {/* Brand Section */}
        <motion.div 
          custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="lg:col-span-1"
        >
          <div className="text-3xl font-extrabold tracking-tighter flex items-center select-none gap-[1px] mb-6">
            <span style={{ color: '#020408', WebkitTextStroke: `1px ${brandGold}`, fontWeight: 900 }}>Stark</span>
            <span className="font-black tracking-tighter" style={{ color: brandGold, fontWeight: 900 }}>xAi</span>
          </div>
          <p className="text-slate-500 text-sm leading-relaxed max-w-sm">
            Pioneering autonomous intelligence for the hospitality and healthcare sectors. We transform stress into intelligent systems.
          </p>
        </motion.div>

        {/* Quick Links */}
        <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <h4 className="text-white font-bold mb-6 uppercase text-xs tracking-widest flex items-center gap-2">
            <Zap size={14} className="text-[#ffb700]" /> Quick Links
          </h4>
          <ul className="text-slate-500 text-sm space-y-4">
            <li>
              <a href="https://starkxrestoservices.onrender.com/" className="group flex items-center hover:text-[#ffb700] transition-all transform hover:translate-x-2 w-fit">
                 <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity -ml-4 group-hover:ml-0 mr-1" />
                 Stark X Restaurant AI
              </a>
            </li>
            <li>
              <a href="https://starkxaihealth.onrender.com" className="group flex items-center hover:text-cyan-400 transition-all transform hover:translate-x-2 w-fit">
                 <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity -ml-4 group-hover:ml-0 mr-1" />
                Stark X Clinical AI
              </a>
            </li>
            <li>
              <a href="comming" className="group flex items-center hover:text-purple-400 transition-all transform hover:translate-x-2 w-fit">
                 <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity -ml-4 group-hover:ml-0 mr-1" />
                Stark X Model Hub <span className="text-[10px] text-slate-600 ml-2 uppercase tracking-wider font-bold bg-white/5 px-2 py-0.5 rounded-full">Soon</span>
              </a>
            </li>
          </ul>
        </motion.div>

        {/* Support */}
        <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <h4 className="text-white font-bold mb-6 uppercase text-xs tracking-widest">Support</h4>
          <ul className="text-slate-500 text-sm space-y-4">
            <li className="hover:text-white transform hover:translate-x-2 cursor-pointer transition-all w-fit">Documentation</li>
            <li className="hover:text-white transform hover:translate-x-2 cursor-pointer transition-all w-fit">Privacy Policy</li>
            <li className="hover:text-white transform hover:translate-x-2 cursor-pointer transition-all w-fit">Terms of Service</li>
          </ul>
        </motion.div>

        {/* Contact Info */}
        <motion.div custom={4} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <h4 className="text-white font-bold mb-6 uppercase text-xs tracking-widest">Contact</h4>
          <div className="space-y-4">
            
            {/* Hyperlinked Email */}
            <a href="mailto:info@starkxai.com" className="flex items-center gap-3 text-slate-500 text-sm hover:text-white transition-colors group w-fit">
              <div className="p-2 rounded-lg bg-white/5 group-hover:bg-[#ffb700]/10 transition-colors">
                <Mail size={16} className="text-[#ffb700]" />
              </div>
              <span>info@starkxai.com</span>
            </a>
            
            {/* Hyperlinked Phone */}
            <a href="tel:+14048201189" className="flex items-center gap-3 text-slate-500 text-sm hover:text-white transition-colors group w-fit">
              <div className="p-2 rounded-lg bg-white/5 group-hover:bg-[#ffb700]/10 transition-colors">
                <Phone size={16} className="text-[#ffb700]" />
              </div>
              <span>+1 (404) 820-1189</span>
            </a>
            
            {/* Location (Not a link, just text) */}
            <div className="flex items-center gap-3 text-slate-500 text-sm w-fit">
              <div className="p-2 rounded-lg bg-white/5">
                <Globe size={16} className="text-[#ffb700]" />
              </div>
              <span>New Jersey, USA</span>
            </div>
            
          </div>
        </motion.div>
      </div>

      {/* Bottom Bar */}
      <motion.div 
        initial={{ opacity: 0 }} 
        whileInView={{ opacity: 1 }} 
        viewport={{ once: true }} 
        transition={{ delay: 0.6, duration: 0.8 }}
        className="max-w-7xl mx-auto pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left"
      >
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-600">
          © 2026 STARKXAI // ALL RIGHTS RESERVED
        </p>
        <div className="flex gap-6 text-slate-600">
            <Zap size={14} className="hover:text-[#ffb700] hover:scale-110 transition-all cursor-pointer" />
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;